import { Fragment, useCallback, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Helmet, HelmetProvider } from 'react-helmet-async';

import { formatNumber } from '../../utils/generalFunctions';
import {
    buscarProyectosActivos,
    buscarVentasPeriodos,
    buscarVentas30Dias,
    buscarPorCobrar
} from '../../actions/dashboard';

import TarjetaMetrica from '../../components/dashboard/TarjetaMetrica';
import ChipVariacion from '../../components/dashboard/ChipVariacion';
import GraficaVentas30Dias from '../../components/dashboard/GraficaVentas30Dias';

const DashboardAdministrador = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const {
        proyectosActivos,
        loadingProyectosActivos,
        errorProyectosActivos,

        ventasPeriodos,
        loadingVentasPeriodos,
        errorVentasPeriodos,

        ventas30Dias,
        loadingVentas30Dias,
        errorVentas30Dias,

        porCobrar,
        loadingPorCobrar,
        errorPorCobrar
    } = useSelector(state => state.dashboard);

    const [actualizando, setActualizando] = useState(false);
    const [ultimaActualizacion, setUltimaActualizacion] = useState(new Date());

    const cargarIndicadores = useCallback(async () => {
        await Promise.all([
            dispatch(buscarProyectosActivos()),
            dispatch(buscarVentasPeriodos()),
            dispatch(buscarVentas30Dias()),
            dispatch(buscarPorCobrar())
        ]);
        setUltimaActualizacion(new Date());
    }, [dispatch]);

    useEffect(() => {
        cargarIndicadores();
    }, [cargarIndicadores]);

    const onActualizar = async () => {
        setActualizando(true);
        await cargarIndicadores();
        setActualizando(false);
    };

    const totalVentas30Dias = (Array.isArray(ventas30Dias) ? ventas30Dias : [])
        .reduce((acc, d) => acc + Number(d?.total || 0), 0);

    const totalCobrar = Number(porCobrar?.total || 0);
    const pdvCobrar = Number(porCobrar?.pdv || 0);
    const proyectosCobrar = Number(porCobrar?.proyectos || 0);
    const pctPdv = totalCobrar > 0 ? (pdvCobrar / totalCobrar) * 100 : 0;
    const pctProyectos = totalCobrar > 0 ? (proyectosCobrar / totalCobrar) * 100 : 0;

    return (
        <Fragment>
            <HelmetProvider>
                <Helmet>
                    <style>{`
                        .esqueleto {
                            height: 16px;
                            border-radius: 4px;
                            background: linear-gradient(90deg, #e9ecef 25%, #f4f5f7 50%, #e9ecef 75%);
                            background-size: 200% 100%;
                            animation: brilloDashboard 1.4s infinite;
                            margin-bottom: 10px;
                        }
                        @keyframes brilloDashboard { to { background-position: -200% 0; } }
                        @media (prefers-reduced-motion: reduce) { .esqueleto { animation: none; } }
                    `}</style>
                </Helmet>
            </HelmetProvider>

            <div className="col-md-12 d-flex justify-content-end align-items-center gap-3 flex-wrap">
                <div className="text-muted" style={{ fontSize: 13 }}>
                    Datos al {ultimaActualizacion.toLocaleDateString('es-MX', { weekday: 'long', day: 'numeric', month: 'long' })}, {ultimaActualizacion.toLocaleTimeString('es-MX', { hour: 'numeric', minute: '2-digit' })}
                </div>
                <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    onClick={onActualizar}
                    disabled={actualizando}
                >
                    <i className={`fas fa-rotate${actualizando ? ' fa-spin' : ''} me-1`} />
                    {actualizando ? 'Actualizando...' : 'Actualizar'}
                </button>
            </div>

            <div className="col-md-3 mt-3">
                <TarjetaMetrica
                    titulo="Ventas de hoy"
                    loading={loadingVentasPeriodos}
                    error={errorVentasPeriodos}
                    valor={<>{formatNumber(ventasPeriodos?.dia?.actual || 0, 2, '$', '')}<small className="text-muted ms-1" style={{ fontSize: 15 }}>MXN</small></>}
                    secundario={<ChipVariacion variacion={ventasPeriodos?.dia?.variacion} etiqueta="vs. periodo anterior equivalente" />}
                />
            </div>

            <div className="col-md-3 mt-3">
                <TarjetaMetrica
                    titulo="Ventas de la semana"
                    loading={loadingVentasPeriodos}
                    error={errorVentasPeriodos}
                    valor={<>{formatNumber(ventasPeriodos?.semana?.actual || 0, 2, '$', '')}<small className="text-muted ms-1" style={{ fontSize: 15 }}>MXN</small></>}
                    secundario={<ChipVariacion variacion={ventasPeriodos?.semana?.variacion} etiqueta="vs. periodo anterior equivalente" />}
                />
            </div>

            <div className="col-md-3 mt-3">
                <TarjetaMetrica
                    titulo="Ventas del mes"
                    loading={loadingVentasPeriodos}
                    error={errorVentasPeriodos}
                    valor={<>{formatNumber(ventasPeriodos?.mes?.actual || 0, 2, '$', '')}<small className="text-muted ms-1" style={{ fontSize: 15 }}>MXN</small></>}
                    secundario={<ChipVariacion variacion={ventasPeriodos?.mes?.variacion} etiqueta="vs. periodo anterior equivalente" />}
                />
            </div>

            <div className="col-md-3 mt-3">
                <TarjetaMetrica
                    titulo="Proyectos activos"
                    variante="oscura"
                    loading={loadingProyectosActivos}
                    error={errorProyectosActivos}
                    valor={proyectosActivos ?? 0}
                    secundario={(
                        <button
                            type="button"
                            className="btn btn-link text-white p-0 mt-2 text-start"
                            style={{ fontSize: 14, textDecoration: 'underline' }}
                            onClick={() => navigate('/proyectos')}
                        >
                            Ver proyectos
                        </button>
                    )}
                />
            </div>

            <div className="col-md-8 mt-3">
                <div className="card h-100">
                    <div className="card-body">
                        <div className="d-flex justify-content-between align-items-baseline flex-wrap gap-2 mb-2">
                            <h6 className="mb-0" style={{ fontSize: 15, fontWeight: 600 }}>Ventas de los últimos 30 días</h6>
                            {!loadingVentasPeriodos && !errorVentas30Dias && (
                                <div className="text-muted" style={{ fontSize: 14 }}>
                                    Total del periodo <strong className="text-dark" style={{ fontSize: 16 }}>{formatNumber(totalVentas30Dias, 2, '$', ' MXN')}</strong>
                                </div>
                            )}
                        </div>

                        <GraficaVentas30Dias data={ventas30Dias} loading={loadingVentas30Dias} error={errorVentas30Dias} />
                    </div>
                </div>
            </div>

            <div className="col-md-4 mt-3">
                <TarjetaMetrica
                    titulo="Por cobrar"
                    loading={loadingPorCobrar}
                    error={errorPorCobrar}
                    valor={<>{formatNumber(totalCobrar, 2, '$', '')}<small className="text-muted ms-1" style={{ fontSize: 15 }}>MXN</small></>}
                    secundario={(
                        <div className="w-100">
                            <div className="d-flex mt-3 mb-2" style={{ height: 12, borderRadius: 6, overflow: 'hidden', background: '#e9ecef' }}>
                                <span style={{ width: `${pctPdv}%`, background: '#1687b5' }} />
                                <span style={{ width: `${pctProyectos}%`, background: '#e8890c' }} />
                            </div>

                            <div className="d-flex justify-content-between align-items-center" style={{ fontSize: 14, borderTop: '1px solid #dee2e6', paddingTop: 10 }}>
                                <span className="text-muted d-flex align-items-center gap-2">
                                    <span style={{ width: 10, height: 10, borderRadius: '50%', display: 'inline-block', background: '#1687b5' }} />
                                    Punto de venta
                                </span>
                                <span className="fw-semibold">{formatNumber(pdvCobrar, 2, '$', '')}</span>
                            </div>
                            <div className="d-flex justify-content-between align-items-center mt-2" style={{ fontSize: 14, borderTop: '1px solid #dee2e6', paddingTop: 10 }}>
                                <span className="text-muted d-flex align-items-center gap-2">
                                    <span style={{ width: 10, height: 10, borderRadius: '50%', display: 'inline-block', background: '#e8890c' }} />
                                    Proyectos
                                </span>
                                <span className="fw-semibold">{formatNumber(proyectosCobrar, 2, '$', '')}</span>
                            </div>

                            <div className="text-muted mt-3" style={{ fontSize: 12 }}>
                                Montos en MXN. Las ventas en USD se convierten con el tipo de cambio de cada venta.
                            </div>
                        </div>
                    )}
                />
            </div>
        </Fragment>
    );
};

export default DashboardAdministrador;
